import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { AlertTriangle, X } from "lucide-react";

/**
 * Minimal slice of the /api/app/billing/summary response this banner needs.
 * The endpoint always returns these founder fields (null for non-founders),
 * so we can safely fail closed on anything that isn't an active founder.
 */
type FounderAllotmentSummary = {
  founderStatus: string | null;
  founderFreeRooms: number | null;
  billedRooms: number;
  founderOverageRateMonthly: number | null;
};

const STORAGE_KEY_PREFIX = "padpulse_founder_allotment_warning_dismissed_";

/**
 * Dismissal is keyed by the (allotment, billed-room) signature rather than a
 * flat per-user flag. Dismissing the warning at "1 room left" must NOT suppress
 * the (more urgent) warning once the host reaches their cap, so each new
 * threshold gets its own dismissal key and the banner re-appears as the founder
 * gets closer to running out of free rooms.
 */
function storageKey(freeRooms: number, billedRooms: number) {
  return `${STORAGE_KEY_PREFIX}${freeRooms}_${billedRooms}`;
}

/**
 * Proactive warning for Founders Circle members who are within 2 billable rooms
 * of their free allotment. Past the allotment every additional room incurs the
 * founder overage rate, so this prevents sticker shock by flagging it before the
 * first overage room is added. Renders nothing for non-founders, for founders
 * still comfortably under their cap, and for founders already in overage (the
 * billing page's overage panel owns that case).
 */
export function FounderAllotmentWarningBanner() {
  const { data } = useQuery<FounderAllotmentSummary>({
    queryKey: ["/api/app/billing/summary"],
    staleTime: 60 * 1000,
    // The banner is purely informational and fails closed when the summary is
    // unavailable, so don't retry — this keeps non-owner viewers (e.g. team
    // members) from generating repeated failed requests for a banner that will
    // never show them anything.
    retry: false,
  });

  const [dismissed, setDismissed] = useState<boolean>(true);

  const isActiveFounder = data?.founderStatus === "active";
  const freeRooms = data?.founderFreeRooms ?? null;
  const billedRooms = data?.billedRooms ?? 0;
  const roomsRemaining = freeRooms != null ? freeRooms - billedRooms : null;

  // Show only when an active founder is within 2 rooms of their cap and has not
  // yet crossed into overage (0, 1, or 2 free rooms remaining).
  const shouldConsiderShowing =
    isActiveFounder &&
    freeRooms != null &&
    roomsRemaining != null &&
    roomsRemaining >= 0 &&
    roomsRemaining <= 2;

  useEffect(() => {
    if (!shouldConsiderShowing || freeRooms == null) {
      setDismissed(true);
      return;
    }
    try {
      setDismissed(localStorage.getItem(storageKey(freeRooms, billedRooms)) === "1");
    } catch {
      setDismissed(false);
    }
  }, [shouldConsiderShowing, freeRooms, billedRooms]);

  if (!shouldConsiderShowing || freeRooms == null || roomsRemaining == null || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey(freeRooms, billedRooms), "1");
    } catch {
      // ignore storage errors; still dismiss in-session
    }
    setDismissed(true);
  };

  const rateMonthly = data?.founderOverageRateMonthly ?? 0;
  const rateLabel = `$${rateMonthly.toFixed(2)}/room/month`;

  const headline =
    roomsRemaining === 0
      ? `You've reached your ${freeRooms}-room free founder allotment.`
      : `You're ${roomsRemaining} room${roomsRemaining === 1 ? "" : "s"} away from your free founder allotment.`;

  const detail =
    roomsRemaining === 0
      ? `You're using all ${freeRooms} of your free rooms. The next billable room you add will start incurring ${rateLabel} in overage charges.`
      : `You're using ${billedRooms} of your ${freeRooms} free rooms. Once you pass ${freeRooms}, each additional billable room is charged ${rateLabel}.`;

  return (
    <div
      className="mb-6 border border-amber-200 bg-amber-50 text-amber-900 rounded-lg"
      data-testid="banner-founder-allotment-warning"
    >
      <div className="px-4 py-3 flex flex-wrap items-center gap-3">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold" data-testid="text-founder-allotment-headline">
            {headline}
          </span>{" "}
          <span className="text-amber-800" data-testid="text-founder-allotment-detail">
            {detail}
          </span>
        </div>
        <Link
          href="/app/account/billing"
          className="text-sm font-medium px-3 py-1.5 rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
          data-testid="link-founder-allotment-billing"
        >
          View billing
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-amber-100"
          aria-label="Dismiss founder room allotment warning"
          data-testid="button-founder-allotment-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
